import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatCZK, plur } from "@/lib/format";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

// Hromadné smazání dokladů (272) — označené v Dokladech (283: dřív vše podle filtru).
// Server bere nejvýš 100 dokladů na volání; posíláme po dávkách a ukazujeme průběh.
// Každý doklad jde stejnou cestou jako koš u řádku (úhrady, sklad, sub-faktura, pokladna, audit).
const CHUNK = 100;
const WORD = "SMAZAT";
const WORD_FROM = 10;    // 283: opsat SMAZAT až od 10 dokladů, pár kusů stačí potvrdit tlačítkem
const KEY = { faktura: "documents", uctenka: "receipts", vydana: "issued" };

export function BulkDeleteDialog({ open, rows, onClose, onDone }) {
  const [word, setWord] = useState("");
  const [done, setDone] = useState(null);   // počet zpracovaných během mazání; null = nemaže se
  useEffect(() => { if (open) { setWord(""); setDone(null); } }, [open]);

  const stats = useMemo(() => {
    const n = { faktura: 0, uctenka: 0, vydana: 0 };
    let total = 0;
    for (const x of rows || []) { n[x.kind] = (n[x.kind] || 0) + 1; total += x.total || 0; }
    return { ...n, total };
  }, [rows]);
  const count = rows?.length || 0;
  const busy = done !== null;
  const needWord = count >= WORD_FROM;
  const ok = !needWord || word.trim().toUpperCase() === WORD;

  const run = async () => {
    const items = (rows || []).filter((x) => KEY[x.kind]);
    setDone(0);
    let deleted = 0, failed = 0;
    try {
      for (let i = 0; i < items.length; i += CHUNK) {
        const body = { documents: [], receipts: [], issued: [] };
        for (const x of items.slice(i, i + CHUNK)) body[KEY[x.kind]].push(x.id);
        const { data } = await api.post("/documents/bulk-delete", body, { timeout: 180000 });
        deleted += Object.values(data.deleted || {}).reduce((s, v) => s + v, 0) + (data.missing || 0);
        failed += data.failed || 0;
        setDone(Math.min(i + CHUNK, items.length));
      }
      if (failed) toast.error(`Smazáno ${plur(deleted, "doklad", "doklady", "dokladů")}, ${plur(failed, "se nepodařil", "se nepodařily", "se nepodařilo")} — zkuste to znovu.`);
      else toast.success(`Smazáno ${plur(deleted, "doklad", "doklady", "dokladů")}`);
    } catch (e) {
      toast.error(`${formatApiError(e.response?.data?.detail) || "Mazání se přerušilo."} Smazáno ${deleted} z ${items.length} — zbytek jde smazat znovu.`);
    } finally {
      setDone(null);
      onDone?.();
    }
  };

  const parts = [
    stats.faktura && plur(stats.faktura, "přijatá faktura", "přijaté faktury", "přijatých faktur"),
    stats.uctenka && plur(stats.uctenka, "účtenka", "účtenky", "účtenek"),
    stats.vydana && plur(stats.vydana, "vydaná faktura", "vydané faktury", "vydaných faktur"),
  ].filter(Boolean);
  const pct = busy && count ? Math.round((done / count) * 100) : 0;

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose?.(); }}>
      <AlertDialogContent data-testid="bulk-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Smazat {plur(count, "doklad", "doklady", "dokladů")}?</AlertDialogTitle>
          <AlertDialogDescription className="sr-only">Hromadné smazání označených dokladů</AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 text-sm">
          <div className="rounded-xl bg-muted/60 px-4 py-3">
            <div className="font-semibold text-foreground" data-testid="bulk-delete-parts">{parts.join(" · ")}</div>
            <div className="text-muted-foreground tabular-nums">celkem {formatCZK(stats.total)} s DPH</div>
          </div>
          <p className="text-muted-foreground">
            Smažou se označené doklady. Zmizí s nimi jejich úhrady a naskladněné položky se odepíšou ze skladu.{" "}
            <b className="text-foreground">Nejde to vrátit.</b>
          </p>
          {busy ? (
            <div className="space-y-1.5" data-testid="bulk-delete-progress">
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-destructive transition-[width] motion-reduce:transition-none" style={{ width: `${pct}%` }} />
              </div>
              <div className="text-muted-foreground tabular-nums">Mažu… {done.toLocaleString("cs-CZ")} z {count.toLocaleString("cs-CZ")}</div>
            </div>
          ) : needWord && (
            <label className="block space-y-1.5">
              <span className="font-semibold">Pro potvrzení napište {WORD}</span>
              <Input value={word} onChange={(e) => setWord(e.target.value)} autoComplete="off"
                className="h-11 w-full min-w-0 text-base md:text-sm" data-testid="bulk-delete-word" />
            </label>
          )}
        </div>

        <AlertDialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="bulk-delete-cancel" className="h-11 w-full sm:w-auto">Zpět</Button>
          <Button variant="destructive" onClick={run} disabled={!ok || busy || !count}
            data-testid="bulk-delete-confirm" className="h-11 w-full sm:w-auto font-bold">
            <Trash2 size={16} /> Smazat {plur(count, "doklad", "doklady", "dokladů")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
